import { defineStore } from 'pinia'
import { ref } from 'vue'
import { sdk } from '@/graphql/client'
import type { MobileInfoDataQuery } from '@/graphql/generated/sdk'

export type InfoData = NonNullable<NonNullable<MobileInfoDataQuery['mobile']>['infoData']>

export const useVerifyStore = defineStore('verify', () => {
    const isOpen = ref(false)
    // True while the verify dialog is actively using the camera — the main
    // ScannerView watches this and releases its own camera stream to avoid conflict.
    const cameraBusy = ref(false)
    const loading = ref(false)
    const error = ref<string | null>(null)
    const result = ref<InfoData | null>(null)
    const lastCode = ref('')

    function open() {
        isOpen.value = true
        result.value = null
        error.value = null
        lastCode.value = ''
        cameraBusy.value = true
    }

    function close() {
        isOpen.value = false
        cameraBusy.value = false
        result.value = null
        error.value = null
        lastCode.value = ''
    }

    function reset() {
        result.value = null
        error.value = null
        lastCode.value = ''
        cameraBusy.value = true
    }

    async function loadInfo(code: string) {
        const trimmed = code.trim()
        if (!trimmed) return
        loading.value = true
        error.value = null
        lastCode.value = trimmed
        // Free the camera while we show the result.
        cameraBusy.value = false
        try {
            const res = await sdk.MobileInfoData({ code: trimmed })
            result.value = res.mobile?.infoData ?? null
            if (!result.value) {
                error.value = 'NOT_FOUND'
            }
        } catch (e) {
            error.value = (e as Error).message || 'ERROR'
            result.value = null
        } finally {
            loading.value = false
        }
    }

    return { isOpen, cameraBusy, loading, error, result, lastCode, open, close, reset, loadInfo }
})
